import { supabase } from "@/integrations/supabase/client";
import { printReceipt, printMultiple, isQZWebSocketActive, PrintJob } from "@/lib/qz-tray";
import { savePrintJobsToDatabase } from "@/lib/centralized-print-queue";
import { printViaRawBT, printMultipleViaRawBT, isAndroidDevice } from "@/lib/rawbt-print";
import { logger } from "@/lib/logger";

export type PrintMode = "qz_tray" | "rawbt" | "mixed" | "local_service";

// In-memory cache for print_mode per restaurant
const printModeCache = new Map<string, { mode: PrintMode; cachedAt: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Get the print mode for a restaurant (with in-memory cache)
 */
export async function getPrintMode(restaurantId: string): Promise<PrintMode> {
  // Check cache first
  const cached = printModeCache.get(restaurantId);
  if (cached && Date.now() - cached.cachedAt < CACHE_TTL_MS) {
    return cached.mode;
  }

  try {
    const { data, error } = await supabase
      .from("restaurants")
      .select("print_mode")
      .eq("id", restaurantId)
      .single();

    const mode = (data?.print_mode as PrintMode) || "qz_tray";
    
    if (!error) {
      printModeCache.set(restaurantId, { mode, cachedAt: Date.now() });
    }

    return mode;
  } catch {
    return "qz_tray";
  }
}

/**
 * Invalidate the print mode cache for a restaurant (call after settings change)
 */
export function invalidatePrintModeCache(restaurantId: string): void {
  printModeCache.delete(restaurantId);
}

interface DispatchPrintOptions {
  /** For logging purposes */
  logType?: string;
  logIdentifier?: string | number;
  /** Location name for queue display */
  locationName?: string;
  /** Order number for queue tracking */
  orderNumber?: number;
  /** Printer settings (for QZ Tray) */
  settings?: any;
}

/**
 * Dispatch a single print job using the restaurant's configured print mode.
 * 
 * - qz_tray: Print directly via QZ Tray WebSocket, or save to centralized queue if disconnected
 * - rawbt: Send directly to RawBT app via URL scheme
 */
export async function dispatchPrint(
  restaurantId: string,
  printerName: string,
  content: string,
  options: DispatchPrintOptions = {}
): Promise<boolean> {
  const mode = await getPrintMode(restaurantId);

  // Fase 4: modo servidor local — o AltusFood Local Server imprime via print-service. O frontend
  // NÃO cria job na nuvem (evita duplicar com o QZ Tray). Loga p/ ser diagnosticável ("por que o
  // QZ não imprime?" tem resposta no console).
  if (mode === "local_service") {
    // console.log (não logger.log): precisa aparecer em PRODUÇÃO — é o diagnóstico de
    // "por que o QZ não imprime?". logger.log é no-op em build de produção do Vite.
    console.log("[dispatcher] print_mode=local_service — impressão delegada ao servidor local");
    return true;
  }

  // RawBT-only mode
  if (mode === "rawbt" && isAndroidDevice()) {
    logger.log(`[Dispatcher] RawBT mode + Android — sending to RawBT`);
    return printViaRawBT(content);
  }

  if (mode === "rawbt" && !isAndroidDevice()) {
    logger.log(`[Dispatcher] RawBT mode but non-Android device — falling through to QZ Tray/queue`);
  }

  // Mixed mode: Android devices use RawBT, others use QZ Tray/queue
  if (mode === "mixed" && isAndroidDevice()) {
    logger.log(`[Dispatcher] Mixed mode + Android — sending to RawBT`);
    return printViaRawBT(content);
  }

  // QZ Tray mode (also mixed mode on non-Android devices)
  if (isQZWebSocketActive()) {
    return await printReceipt(printerName, content, {
      logType: options.logType,
      logIdentifier: options.logIdentifier,
      settings: options.settings,
    });
  }

  // QZ not connected — save to centralized queue
  logger.log(`[Dispatcher] QZ Tray disconnected — saving to queue`);
  return await savePrintJobsToDatabase(restaurantId, [{
    orderNumber: options.orderNumber || 0,
    printerName,
    content,
    locationName: options.locationName,
  }]);
}

/**
 * Dispatch multiple print jobs.
 * 
 * - qz_tray: Batch print via QZ Tray, or save all to queue if disconnected
 * - rawbt: Send each job sequentially to RawBT with delays
 */
export async function dispatchMultiplePrints(
  restaurantId: string,
  jobs: PrintJob[],
  orderNumber?: number
): Promise<boolean> {
  if (jobs.length === 0) return true;

  const mode = await getPrintMode(restaurantId);

  // Fase 4: modo servidor local — o local imprime; o frontend não cria jobs na nuvem.
  if (mode === "local_service") {
    // console.log (não logger.log): visível em produção (diagnóstico). Ver dispatchPrint acima.
    console.log(`[dispatcher] print_mode=local_service — ${jobs.length} impressão(ões) delegada(s) ao servidor local`);
    return true;
  }

  // RawBT-only mode
  if (mode === "rawbt" && isAndroidDevice()) {
    logger.log(`[Dispatcher] RawBT mode + Android — sending ${jobs.length} job(s)`);
    const contents = jobs.map(j => j.content);
    return await printMultipleViaRawBT(contents);
  }

  if (mode === "rawbt" && !isAndroidDevice()) {
    logger.log(`[Dispatcher] RawBT mode but non-Android device — falling through to QZ Tray/queue`);
  }

  // Mixed mode: Android devices use RawBT, others use QZ Tray/queue
  if (mode === "mixed" && isAndroidDevice()) {
    logger.log(`[Dispatcher] Mixed mode + Android — sending ${jobs.length} job(s) via RawBT`);
    const contents = jobs.map(j => j.content);
    return await printMultipleViaRawBT(contents);
  }

  // QZ Tray mode (also mixed mode on non-Android devices)
  if (isQZWebSocketActive()) {
    await printMultiple(jobs);
    return true;
  }

  // QZ not connected — save all to centralized queue
  logger.log(`[Dispatcher] QZ Tray disconnected — saving ${jobs.length} job(s) to queue`);
  return await savePrintJobsToDatabase(
    restaurantId,
    jobs.map(job => ({
      orderNumber: orderNumber || 0,
      printerName: job.printerName,
      content: job.content,
      locationName: job.locationName,
    }))
  );
}
